// components/FriendRequestButton.jsx
import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
// Create this CSS file separately
import './FriendRequestButton.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

// Helper function to check friendship status
const fetchFriendshipStatus = async (userId, token) => {
  try {
    // Get all friends
    const friendsResponse = await axios.get(`${API_BASE_URL}/api/friends`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    
    const friends = friendsResponse.data.data || [];
    // Check both profile_picture and profilePicture to handle both formats
    const existingFriend = friends.find(f => 
      String(f.user.id) === String(userId) || 
      (f.user._id && String(f.user._id) === String(userId))
    );
    
    if (existingFriend) {
      return { 
        status: 'friends', 
        friendshipId: existingFriend.friendshipId 
      };
    }
    
    // Check sent requests
    const sentResponse = await axios.get(`${API_BASE_URL}/api/friends/sent`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    
    const sentRequests = sentResponse.data.data || [];
    const pendingSent = sentRequests.find(r => r.recipient.id === userId);
    
    if (pendingSent) {
      return { 
        status: 'sent', 
        requestId: pendingSent.id 
      };
    }
    
    // Check pending requests
    const pendingResponse = await axios.get(`${API_BASE_URL}/api/friends/pending`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    
    const pendingRequests = pendingResponse.data.data || [];
    const pendingReceived = pendingRequests.find(r => r.requester.id === userId);
    
    if (pendingReceived) {
      return { 
        status: 'pending', 
        requestId: pendingReceived.id 
      };
    }
    
    return { status: 'none' };
  } catch (error) {
    console.error('Error checking friendship status:', error);
    return { status: 'error' };
  }
};

const FriendRequestButton = ({ userId }) => {
  const { isLoggedIn, user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();
  const token = localStorage.getItem('token');
  const isSelfProfile = user?.id === userId;
  
  // This hook is now called unconditionally at the top level
  const { data: friendshipStatus, isLoading: statusLoading } = useQuery({
    queryKey: ['friendshipStatus', userId],
    queryFn: () => fetchFriendshipStatus(userId, token),
    enabled: isLoggedIn && !!userId && !isSelfProfile
  });
  
  // Send friend request - now called unconditionally
  const sendRequestMutation = useMutation({
    mutationFn: async () => {
      setIsLoading(true);
      try {
        const response = await axios.post(
          `${API_BASE_URL}/api/friends/${userId}`,
          {},
          { headers: { Authorization: `Bearer ${token}` }}
        );
        return response.data;
      } catch (error) {
        console.error('Error sending friend request:', error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['friendshipStatus', userId]);
    }
  });
  
  // Accept friend request - now called unconditionally
  const acceptRequestMutation = useMutation({
    mutationFn: async (requestId) => {
      setIsLoading(true);
      try {
        const response = await axios.patch(
          `${API_BASE_URL}/api/friends/${requestId}`,
          { status: 'accepted' },
          { headers: { Authorization: `Bearer ${token}` }}
        );
        return response.data;
      } catch (error) {
        console.error('Error accepting friend request:', error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['friendshipStatus', userId]);
      queryClient.invalidateQueries(['friends']);
      queryClient.invalidateQueries(['friendRequests', 'pending']);
    }
  });
  
  // Cancel/Remove friend - now called unconditionally
  const removeFriendMutation = useMutation({
    mutationFn: async (id) => {
      setIsLoading(true);
      try {
        const response = await axios.delete(
          `${API_BASE_URL}/api/friends/${id}`,
          { headers: { Authorization: `Bearer ${token}` }}
        );
        return response.data;
      } catch (error) {
        console.error('Error removing friend/canceling request:', error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['friendshipStatus', userId]);
      queryClient.invalidateQueries(['friends']);
      queryClient.invalidateQueries(['friendRequests', 'sent']);
    }
  });
  
  // Early returns based on conditions
  if (!isLoggedIn || isSelfProfile) {
    return null;
  }
  
  if (statusLoading || isLoading) {
    return <button className="friend-button loading" disabled>Loading...</button>;
  }
  
  // Render different buttons based on friendship status
  if (friendshipStatus?.status === 'friends') {
    return (
      <div className="friend-status-container">
        <button 
          className="friend-button is-friend"
          onClick={() => removeFriendMutation.mutate(friendshipStatus.friendshipId)}
        >
          ✓ Friends • Remove
        </button>
      </div>
    );
  }
  
  if (friendshipStatus?.status === 'sent') {
    return (
      <button 
        className="friend-button request-sent"
        onClick={() => removeFriendMutation.mutate(friendshipStatus.requestId)}
      >
        Cancel Request
      </button>
    );
  }
  
  if (friendshipStatus?.status === 'pending') {
    return (
      <button 
        className="friend-button accept-request"
        onClick={() => acceptRequestMutation.mutate(friendshipStatus.requestId)}
      >
        Accept Request
      </button>
    );
  }
  
  return (
    <button 
      className="friend-button add-friend"
      onClick={() => sendRequestMutation.mutate()}
    >
      Add Friend
    </button>
  );
};

export default FriendRequestButton;